
import React from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface MobileStatsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  colorScheme?: 'primary' | 'blue' | 'green' | 'purple' | 'orange';
  className?: string;
}

const colorSchemes = {
  primary: {
    icon: 'bg-primary/15 text-primary border-primary/20',
    card: 'hover:bg-primary/5'
  },
  blue: {
    icon: 'bg-blue-50 text-blue-600 border-blue-200/50',
    card: 'hover:bg-blue-50/30'
  },
  green: {
    icon: 'bg-green-50 text-green-600 border-green-200/50',
    card: 'hover:bg-green-50/30'
  },
  purple: {
    icon: 'bg-purple-50 text-purple-600 border-purple-200/50',
    card: 'hover:bg-purple-50/30'
  },
  orange: {
    icon: 'bg-orange-50 text-orange-600 border-orange-200/50',
    card: 'hover:bg-orange-50/30'
  }
};

export const MobileStatsCard = ({ 
  title, 
  value, 
  icon, 
  colorScheme = 'primary',
  className
}: MobileStatsCardProps) => {
  const colors = colorSchemes[colorScheme];
  
  return (
    <Card className={cn(
      "group relative aspect-square cursor-pointer border border-border/50",
      "bg-gradient-to-br from-background via-background to-muted/5",
      "transition-all duration-300 ease-out",
      "hover:shadow-md hover:shadow-black/5 hover:-translate-y-0.5",
      "hover:border-border/70 active:scale-[0.98]",
      colors.card,
      className
    )}>
      <div className="relative h-full p-4 flex flex-col items-center justify-center text-center">
        {/* Icon */}
        {icon && (
          <div className={cn(
            "flex items-center justify-center rounded-xl mb-3 border transition-all duration-300",
            "w-12 h-12 group-hover:scale-105",
            colors.icon
          )}>
            {React.cloneElement(icon as React.ReactElement, { 
              size: 20,
              className: "transition-transform duration-300 group-hover:rotate-3"
            })}
          </div>
        )}
        
        {/* Value */}
        <div className="mb-2">
          <h3 className="text-2xl font-bold text-foreground transition-colors duration-300">
            {value}
          </h3>
        </div>
        
        {/* Title */}
        <p className="text-xs font-medium text-muted-foreground leading-tight transition-colors duration-300">
          {title}
        </p>
        
        {/* Subtle touch feedback overlay */}
        <div className="absolute inset-0 rounded-lg opacity-0 group-active:opacity-100 transition-opacity duration-150 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
      </div>
    </Card>
  );
};
